import { GoalType, IconFormNames } from '@griply/types';
import {
  getDirectGoalLifeAreaIds,
  getGoalParentIds,
  getNotAllowedParentGoalIds,
  resolveMovedLinkIds,
  resolveReplacedGoalLinks,
} from '@griply/utils';
import {
  useCreateGoal,
  useCreateUnlimitedGoalsAllowed,
  useGoalById,
  useMoveGoal,
  useSortedGoals,
} from 'features/goals';
import {
  useCreateCustomLifeAreasAllowed,
  useCreateLifeArea,
  useSortedLocalizedLifeAreas,
} from 'features/life-areas';
import React, { useMemo } from 'react';
import { useMatch } from 'react-router-dom';
import {
  GoalTreeCreateOption,
  GoalTreeMenu,
  GoalTreeMenuSelection,
} from 'shared/components/ui/goal-tree-menu';
import { useOpenPremiumDialog } from 'shared/contexts/premium-dialog';
import { useEntityLinks } from 'shared/hooks/use-entity-links';
import { Paths } from 'shared/routes/paths';
import { ID } from 'shared/types/id';

export type GoalTreeContextMenuProps = Omit<
  React.ComponentProps<typeof GoalTreeMenu>,
  | 'goals'
  | 'lifeAreas'
  | 'onSelect'
  | 'selection'
  | 'expandTo'
  | 'disabledGoalIds'
  | 'onCreate'
  | 'isCreatePremium'
  | 'links'
> & {
  /** The goal being moved. Omit when reusing the menu as a pure picker via `onSelect`. */
  id?: ID;
  /** Omit to mutate the goal directly; provide it to own the persistence (e.g. until form Save). */
  onSelect?: (ancestorIds: ID[], lifeAreaId: ID | null) => void;
  /** The already-picked target, highlighted but still selectable and expandable. */
  currentSelection?: { goalId?: ID; lifeAreaId?: ID };
  /** The link the row the menu was opened from sits under — that is the one a move replaces. */
  sourceLink?: { goalId?: ID; lifeAreaId?: ID };
  /** Stages the goal instead of persisting it (drafts create it on submit). Life areas always persist. */
  onCreateGoal?: (goal: { name: string; type?: GoalType }) => void;
};

export const GoalTreeContextMenu: React.FC<GoalTreeContextMenuProps> = ({
  id,
  onSelect,
  currentSelection,
  sourceLink,
  onCreateGoal,
  onClose,
  ...rest
}) => {
  const goals = useSortedGoals();
  const lifeAreas = useSortedLocalizedLifeAreas({ activeOnly: true });
  const goal = useMemo(() => goals.find((goal) => goal.id === id), [goals, id]);

  const moveGoal = useMoveGoal();
  const links = useEntityLinks();
  const { submit: createGoal } = useCreateGoal();
  const { submit: createLifeArea } = useCreateLifeArea();
  const { allowed: createGoalAllowed } = useCreateUnlimitedGoalsAllowed();
  const { allowed: createProjectAllowed } = useCreateUnlimitedGoalsAllowed(
    GoalType.Project,
  );
  const { allowed: createLifeAreaAllowed } = useCreateCustomLifeAreasAllowed();
  const openPremiumDialog = useOpenPremiumDialog();

  const isCreatePremium = {
    [GoalTreeCreateOption.LifeArea]: !createLifeAreaAllowed,
    [GoalTreeCreateOption.Goal]: !createGoalAllowed,
    [GoalTreeCreateOption.Project]: !createProjectAllowed,
  };

  // A goal created from the menu lands in the life area of the page it was opened on.
  const lifeAreaDetailMatch = useMatch({
    path: Paths.LifeAreaDetail,
    end: false,
  });
  const goalDetailMatch = useMatch({ path: Paths.GoalDetail, end: false });
  const projectDetailMatch = useMatch({
    path: Paths.ProjectDetail,
    end: false,
  });
  const pageGoal = useGoalById(
    goalDetailMatch?.params.id ?? projectDetailMatch?.params.id,
  );
  const pageLifeAreaId =
    lifeAreaDetailMatch?.params.id ?? pageGoal?.lifeAreaId ?? null;

  const currentParentIds = goal ? getGoalParentIds(goal, { links }) : [];
  const currentLifeAreaIds = goal ? getDirectGoalLifeAreaIds(goal, links) : [];

  // A move replaces the link of the row it was opened from; the goal's other links stay.
  const { movedParentId: parentId, movedLifeAreaId } = resolveMovedLinkIds({
    row: sourceLink,
    currentParentIds,
    currentLifeAreaIds,
  });

  const onSelectTarget = (ancestorIds: ID[], lifeAreaId: ID | null) => {
    if (onSelect) {
      onSelect(ancestorIds, lifeAreaId);
      return;
    }
    if (!goal) {
      return;
    }
    // If we move to a parent that is archived, we should also archive this goal
    const newParentId = ancestorIds[ancestorIds.length - 1];
    const newParent = goals.find((g) => g.id === newParentId);
    const shouldArchive = !!newParent?.archivedAt && !goal.archivedAt;

    // `ancestorIds` stays frozen — the move writes the relationship document plus the two flags.
    moveGoal(goal, {
      ...resolveReplacedGoalLinks({
        targetParentId: newParentId,
        targetLifeAreaId: lifeAreaId,
      }),
      archive: shouldArchive,
    });
  };

  const onCreate = async (name: string, option: GoalTreeCreateOption) => {
    if (isCreatePremium[option]) {
      openPremiumDialog();
      onClose();
      return;
    }

    if (option === GoalTreeCreateOption.LifeArea) {
      const lifeArea = await createLifeArea({
        name,
        iconName: IconFormNames.PlaceholderIcon,
      });
      onSelectTarget([], lifeArea.id);
      onClose();
      return;
    }

    const typeFields =
      option === GoalTreeCreateOption.Project ? { type: GoalType.Project } : {};

    if (onCreateGoal) {
      onCreateGoal({ name, ...typeFields });
      onClose();
      return;
    }

    const createdGoal = await createGoal(
      { name, lifeAreaId: pageLifeAreaId, ...typeFields },
      {
        lifeArea: lifeAreas.find(({ id }) => id === pageLifeAreaId),
        links: {
          parentIds: [],
          lifeAreaIds: pageLifeAreaId ? [pageLifeAreaId] : [],
        },
      },
    );

    onSelectTarget([createdGoal.id], pageLifeAreaId);
    onClose();
  };

  const selection: GoalTreeMenuSelection = currentSelection ?? {
    goalId: parentId,
    lifeAreaId: movedLifeAreaId,
    noLifeArea: !parentId && !movedLifeAreaId,
  };

  const disabledGoalIds = useMemo(() => {
    // The picked item stays enabled so its children remain reachable; re-picking it is a no-op.
    if (currentSelection) {
      return new Set<ID>();
    }
    return id
      ? getNotAllowedParentGoalIds(id, { goals, links })
      : new Set<ID>();
  }, [currentSelection, id, goals, links]);

  return (
    <GoalTreeMenu
      {...rest}
      goals={goals}
      lifeAreas={lifeAreas}
      links={links}
      selection={selection}
      expandTo={{ goalId: goal?.id }}
      disabledGoalIds={disabledGoalIds}
      onSelect={onSelectTarget}
      onCreate={onCreate}
      isCreatePremium={isCreatePremium}
      onClose={onClose}
    />
  );
};
